import type { ILegalTransaction_has_LegalTransactionSpecialFunction } from '../../../Interfaces/ILegalTransaction_has_LegalTransactionSpecialFunction';
import type { ILegalRequirementState } from '../../../Interfaces/ILegalRequirementState';
import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { IFunctionAttribute } from '../../../Interfaces/ILegalTransactionMeasure';
import type { ILegalPhaseFullObject } from '../../../Interfaces/ILegalPhase';
import type { ILegalTransactionGwGFullObject } from '../../../Interfaces/ILegalTransactionGwG';
import { HandelsregisterCheck } from '../../LegalTransactionFunctions/HandelsregisterCheck/HandelsregisterCheck';
import { GwGMain } from '../../LegalTransactionFunctions/GwG/GwGMain';
import { ShareholderListCheck } from '../../LegalTransactionFunctions/ShareHolderList/ShareHolderListCheck';
import { Typography, useTheme } from '@mui/material';
import { Box } from '@mui/material';

type IProps = {
  attribute: IFunctionAttribute;
  idLegalTransaction: number;
  legalTransactionHasSpecialFunctions: ILegalTransaction_has_LegalTransactionSpecialFunction[];
  setLegalTransactionHasSpecialFunctions: Dispatch<
    SetStateAction<ILegalTransaction_has_LegalTransactionSpecialFunction[]>
  >;
  gwgs: ILegalTransactionGwGFullObject[];
  setGwgs: Dispatch<SetStateAction<ILegalTransactionGwGFullObject[]>>;
  legalPhaseFullObjects: ILegalPhaseFullObject[];
  legalRequirementStates: ILegalRequirementState[];
};

export const FunctionAttribute = ({
  attribute,
  idLegalTransaction,
  legalTransactionHasSpecialFunctions,
  setLegalTransactionHasSpecialFunctions,
  gwgs,
  setGwgs,
  legalPhaseFullObjects,
  legalRequirementStates,
}: IProps) => {
  const theme = useTheme();
  const [
    legalTransactionHasSpecialFunctionsFiltered,
    setLegalTransactionHasSpecialFunctionsFiltered,
  ] = useState<ILegalTransaction_has_LegalTransactionSpecialFunction[]>([]);

  const addSpecialFunction = (idSF: number) => {
    let minId = -1;
    if (legalTransactionHasSpecialFunctions.length > 0) {
      const minIdArray =
        Math.min(
          ...legalTransactionHasSpecialFunctions.map(
            x => x.idLegalTransaction_has_LegalTransactionSpecialFunction
          )
        ) - 1;

      if (minIdArray <= minId) {
        minId = minIdArray - 1;
      }
    }

    const newObject = {
      idLegalTransaction_has_LegalTransactionSpecialFunction: minId,
      idLegalTransaction: idLegalTransaction,
      idLegalRequirement: -1,
      idLegalRequirementState: -1,
      idSF: idSF,
      Function_Field1: new Date().toISOString().split('T')[0],
      Function_Field2: '',
      Function_Field3: '',
      Function_Field4: '',
      Function_Field5: '',
      Function_Field6: '',
      Res_Field1: '',
    } as ILegalTransaction_has_LegalTransactionSpecialFunction;

    setLegalTransactionHasSpecialFunctions([
      ...legalTransactionHasSpecialFunctions,
      newObject,
    ]);

    return minId;
  };

  const [gwgsFiltered, setGwgsFiltered] = useState<
    ILegalTransactionGwGFullObject[]
  >([]);

  useEffect(() => {
    if (!attribute.Value) {
      if (attribute.idSF !== 2) {
        const minId = addSpecialFunction(attribute.idSF);
        attribute.Value = minId;
      } else if (attribute.idSF === 2) {
        let minId = -1;
        if (gwgs.length > 0) {
          const minIdArray =
            Math.min(...gwgs.map(x => x.idLegalTransactionGwG)) - 1;

          if (minIdArray <= minId) {
            minId = minIdArray - 1;
          }
        }
        const newObject = {
          idLegalTransactionGwG: minId,
          idLegalTransaction: -1,
          idLegalRequirement: null,
          LegalTransactionGwGEntryArray: [],
          total: 0,
          title: 'GwG Kaufvertrag',
        };
        setGwgs([...gwgs, newObject]);
        attribute.Value = minId;
      }
    } else {
      if (attribute.idSF !== 2) {
        setLegalTransactionHasSpecialFunctionsFiltered(
          legalTransactionHasSpecialFunctions.filter(
            item =>
              item.idLegalTransaction_has_LegalTransactionSpecialFunction ===
              attribute.Value
          )
        );
      } else {
        setGwgsFiltered(
          gwgs.filter(item => item.idLegalTransactionGwG === attribute.Value)
        );
      }
    }
  }, [attribute.Value, gwgs, legalTransactionHasSpecialFunctions]);

  const handleFieldChangeOnImport = (
    index: number,
    updates: { [key: string]: string }
  ) => {
    //find the index of the filtered item in the original array
    const originalIndex = legalTransactionHasSpecialFunctions.findIndex(
      item =>
        item.idLegalTransaction_has_LegalTransactionSpecialFunction ===
        legalTransactionHasSpecialFunctionsFiltered[index]
          .idLegalTransaction_has_LegalTransactionSpecialFunction
    );
    const updatedArray = legalTransactionHasSpecialFunctions.map((item, i) => {
      if (i === originalIndex) {
        const updatedItem = { ...item, ...updates };
        if (updatedItem.idSF === 3) {
          updatedItem.Function_Field3 = '1';
        }
        if (updatedItem.idSF === 1) {
          const followUpState =
            Number(updatedItem.Function_Field3) === 2 ? 2 : 1;
          updatedItem.Function_Field3 = followUpState.toString();
        }
        return updatedItem;
      }
      return item;
    });
    setLegalTransactionHasSpecialFunctions(updatedArray);
  };

  const setLegalTransactionHasSpecialFunctionsAltered = (
    updatedFilteredArray: ILegalTransaction_has_LegalTransactionSpecialFunction[]
  ) => {
    const updatedArray = legalTransactionHasSpecialFunctions.map(item => {
      const updatedItem = updatedFilteredArray.find(
        updatedItem =>
          updatedItem.idLegalTransaction_has_LegalTransactionSpecialFunction ===
          item.idLegalTransaction_has_LegalTransactionSpecialFunction
      );
      if (updatedItem) {
        return updatedItem;
      }
      return item;
    });
    setLegalTransactionHasSpecialFunctions(updatedArray);
  };

  const setGwgsAltered = (
    updatedFilteredArray: ILegalTransactionGwGFullObject[]
  ) => {
    const updatedArray = gwgs.map(item => {
      const updatedItem = updatedFilteredArray.find(
        updatedItem =>
          updatedItem.idLegalTransactionGwG === item.idLegalTransactionGwG
      );
      if (updatedItem) {
        return updatedItem;
      }
      return item;
    });
    setGwgs(updatedArray);
  };

  const deleteSpecialFunctionAltered = (index: number) => {
    const updatedArray = legalTransactionHasSpecialFunctions.filter(
      item =>
        item.idLegalTransaction_has_LegalTransactionSpecialFunction !==
        legalTransactionHasSpecialFunctionsFiltered[index]
          .idLegalTransaction_has_LegalTransactionSpecialFunction
    );
    setLegalTransactionHasSpecialFunctions(updatedArray);
    attribute.Value = undefined;
  };

  switch (attribute.Name) {
    case 'Handelsregister Nachverfolgung':
      return (
        <HandelsregisterCheck
          handleFieldChangeOnImport={handleFieldChangeOnImport}
          idLegalTransaction={idLegalTransaction}
          legalTransaction_has_LegalTransactionSpecialFunctionArray={
            legalTransactionHasSpecialFunctionsFiltered
          }
          setLegalTransaction_has_LegalTransactionSpecialFunctionArray={
            setLegalTransactionHasSpecialFunctionsAltered
          }
          legalPhaseFullObjectArray={legalPhaseFullObjects}
          legalReqState={legalRequirementStates}
          deleteSpecialFunction={deleteSpecialFunctionAltered}
        />
      );
    case 'GwG':
      return (
        <GwGMain
          gwgArray={gwgsFiltered}
          setGwgArray={setGwgsAltered}
          legalPhaseFullObjectArray={legalPhaseFullObjects}
        />
      );
    case 'Gesellschafterliste Nachverfolgung':
      return (
        <ShareholderListCheck
          handleFieldChangeOnImport={handleFieldChangeOnImport}
          idLegalTransaction={idLegalTransaction}
          legalTransaction_has_LegalTransactionSpecialFunctionArray={
            legalTransactionHasSpecialFunctionsFiltered
          }
          setLegalTransaction_has_LegalTransactionSpecialFunctionArray={
            setLegalTransactionHasSpecialFunctionsAltered
          }
          legalPhaseFullObjectArray={legalPhaseFullObjects}
          legalReqState={legalRequirementStates}
          deleteSpecialFunction={deleteSpecialFunctionAltered}
        />
      );

    case 'Grundbuch Nachverfolgung':
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            justifyContent: 'center',
            alignItems: 'center',
            height: '40vh',
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: 2,
            backgroundColor: 'white',
            p: 2,
          }}
        >
          <Typography variant='h6'>Grundbuch Nachverfolgung</Typography>
          <Typography>Diese Funktion ist derzeit nicht verfügbar.</Typography>
        </Box>
        // <LandRegisterCheck
        //   idLegalTransaction={idLegalTransaction}
        //   legalTransaction_has_LegalTransactionSpecialFunctionArray={
        //     legalTransactionHasSpecialFunctionsFiltered
        //   }
        //   setLegalTransaction_has_LegalTransactionSpecialFunctionArray={
        //     setLegalTransactionHasSpecialFunctionsAltered
        //   }
        //   legalPhaseFullObjectArray={legalPhaseFullObjects}
        //   legalReqState={legalRequirementStates}
        //   deleteSpecialFunction={deleteSpecialFunctionAltered}
        // />
      );

    default:
      return null;
  }
};
